/*
    The rows behind "Revenue by journey step" (MPN-1836).

    Verifying a revenue figure used to mean opening the Pricesheet page, finding the step's
    price, and dividing: $3,150 / $450 = 7 conversions. Everything needed to show that
    outright was already in the payload -- `conversions`, `step_price`, `base_price` -- and
    the component was dropping all three.

    WHAT "STEP PRICE" HAS TO MEAN HERE. A billable event is priced at `base_price +
    step_price` (see billable_events.py), so revenue divided by conversions is the pair, not
    the step price alone. Showing `step_price` by itself would leave the row not adding up on
    every engagement that has a base price -- and the ticket's own acceptance test is that it
    adds up: 7 x $450 = $3,150. So the column shows what each conversion actually earned, and
    says so when a base price is part of it.

    Extracted from the component so the arithmetic can be tested without a DOM.
*/

export interface RevenueStepRow {
    /** Stable across renders: a dimensioned step yields several rows sharing a name. */
    key: string;
    name: string;
    conversions: number;
    /** What one conversion earned: base price plus step price. */
    pricePerConversion: number;
    /** The step's own price, when a base price is also in play. Null when there is none. */
    basePrice: number | null;
    revenue: number;
    /**
     * Whether conversions x price does not equal revenue.
     *
     * Rachel asked whether this was worth flagging. It is, but only when it means something:
     * with the effective price above, a mismatch is a genuine signal that the events behind
     * one step were not all priced the same -- which is exactly the pricing discrepancy this
     * ticket came out of. A row that never foots would be a warning nobody reads.
     */
    mismatched: boolean;
}

/** A cent, in dollars. Money is summed server-side, so only rounding noise should survive. */
const TOLERANCE = 0.01;

export function toRevenueRows(payload: unknown): RevenueStepRow[] {
    const steps = (payload as any)?.["revenue-by-journey-step"] ?? [];
    if (!Array.isArray(steps)) return [];

    return steps.map((item: any, index: number) => {
        const conversions = Number(item?.conversions ?? 0);
        const revenue = Number(item?.revenue ?? 0);
        const step = Number(item?.step_price ?? 0);
        const base = Number(item?.base_price ?? 0);
        const pricePerConversion = step + base;

        return {
            // `step_name` is not unique -- one row per (step, dimension) -- so it cannot be
            // the key on its own.
            key: `${item?.step_name ?? "step"}-${item?.journey_step_dimension_id ?? index}`,
            name: item?.step_name ?? "Unknown",
            conversions,
            pricePerConversion,
            basePrice: base > 0 ? base : null,
            revenue,
            mismatched:
                conversions > 0
                && Math.abs(conversions * pricePerConversion - revenue) > TOLERANCE,
        };
    });
}

export const money = (value: number) =>
    `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
